import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, errMsg, fmtDate, STATUS } from "@/lib/api";
import { PageHeader, Spinner, StatusBadge } from "@/components/bits";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { AlertTriangle, ArrowRight, Building2, ChevronDown, Loader2, Plus, RefreshCw, Ticket, TicketCheck } from "lucide-react";
import { toast } from "sonner";

export default function TenantDashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [empre, setEmpre] = useState(null);
  const [eqlStatus, setEqlStatus] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    api.get("/tickets/stats").then(({ data }) => setStats(data)).catch(() => setStats({}));
    api.get("/tickets").then(({ data }) => setRecent(data.slice(0, 6))).catch(() => {});
    api.get("/tenant/brave/empreendimento").then(({ data }) => {
      setEmpre(data);
      api.get("/tenant/brave/eqls/status").then(({ data: st }) => {
        setEqlStatus(st);
        if (!st.count && !st.syncing) {
          api.post("/tenant/brave/eqls/sync")
            .then(() => setEqlStatus((s) => ({ ...(s || {}), syncing: true })))
            .catch(() => {});
        }
      }).catch(() => {});
    }).catch(() => {});
  }, []);

  const refreshEqls = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await api.post("/tenant/brave/eqls/sync");
      const started = Date.now();
      const poll = async () => {
        try {
          const { data } = await api.get("/tenant/brave/eqls/status");
          setEqlStatus(data);
          if (!data.syncing) {
            setRefreshing(false);
            toast.success(`Lista de EQLs atualizada (${data.count})`);
            return;
          }
        } catch {
          setRefreshing(false);
          return;
        }
        if (Date.now() - started > 120000) {
          setRefreshing(false);
          return;
        }
        setTimeout(poll, 2000);
      };
      setTimeout(poll, 2000);
    } catch (e) {
      toast.error(errMsg(e));
      setRefreshing(false);
    }
  };

  if (!stats) return <Spinner />;

  return (
    <div data-testid="tenant-dashboard">
      <PageHeader title="Dashboard" subtitle="Resumo da operação de suporte">
        <ThemeToggle tid="theme-toggle-dashboard" />
        <Link to="/app/novo">
          <Button data-testid="dashboard-new-ticket-button" className="bg-indigo-600 hover:bg-indigo-500 text-white rounded-md transition-colors duration-200">
            <Plus className="h-4 w-4 mr-1.5" /> Novo Chamado
          </Button>
        </Link>
      </PageHeader>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4" data-testid="stat-total">
          <div className="h-8 w-8 rounded-md bg-indigo-400/10 flex items-center justify-center mb-3"><TicketCheck className="h-4 w-4 text-indigo-400" /></div>
          <p className="text-2xl font-semibold text-zinc-100">{stats.total}</p>
          <p className="text-xs text-zinc-500 mt-1">Chamados no total</p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4 hidden md:block" data-testid="stat-open">
          <div className="h-8 w-8 rounded-md bg-amber-400/10 flex items-center justify-center mb-3"><Ticket className="h-4 w-4 text-amber-400" /></div>
          <p className="text-2xl font-semibold text-zinc-100">{stats.open}</p>
          <p className="text-xs text-zinc-500 mt-1">Em aberto</p>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-md p-4 hidden md:block" data-testid="stat-critical">
          <div className="h-8 w-8 rounded-md bg-rose-400/10 flex items-center justify-center mb-3"><AlertTriangle className="h-4 w-4 text-rose-400" /></div>
          <p className="text-2xl font-semibold text-zinc-100">{stats.critical_open}</p>
          <p className="text-xs text-zinc-500 mt-1">Críticos em aberto</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5">
          <h2 className="text-sm font-medium text-zinc-100 mb-4">Por status</h2>
          <div className="space-y-2.5">
            {Object.entries(STATUS).map(([key, label]) => (
              <div key={key} className="flex items-center justify-between text-sm">
                <StatusBadge value={key} />
                <span className="text-zinc-300 font-medium">{stats.by_status?.[key] || 0}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="lg:col-span-2 bg-zinc-900 border border-zinc-800 rounded-md p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-medium text-zinc-100">Chamados recentes</h2>
            <Link to="/app/chamados" className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1">
              Ver todos <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          {recent.length === 0 ? (
            <p className="text-sm text-zinc-500 py-6 text-center">Nenhum chamado ainda.</p>
          ) : (
            <div className="divide-y divide-zinc-800/60">
              {recent.map((t) => (
                <Link key={t.id} to={`/app/chamados/${t.id}`} data-testid={`recent-ticket-${t.id}`}
                  className="flex items-center gap-3 py-2.5 hover:bg-zinc-800/40 -mx-2 px-2 rounded transition-colors duration-200">
                  <span className="font-mono text-xs text-zinc-500 w-14">#{String(t.number).padStart(4, "0")}</span>
                  <span className="text-sm text-zinc-200 truncate flex-1">{t.title}</span>
                  <span className="text-xs text-zinc-500 hidden md:inline">{t.client_name}</span>
                  <StatusBadge value={t.status} />
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {empre && (
        <Collapsible className="mt-6 bg-zinc-900 border border-zinc-800 rounded-md" data-testid="empreendimento-collapsible">
          <CollapsibleTrigger data-testid="empreendimento-toggle"
            className="w-full flex items-center gap-2 px-5 py-3.5 text-sm font-medium text-zinc-100 hover:bg-zinc-800/40 rounded-md transition-colors duration-200 [&[data-state=open]>svg.chev]:rotate-180">
            <Building2 className="h-4 w-4 text-indigo-400 shrink-0" />
            <span className="truncate flex-1 text-left" data-testid="empreendimento-title">{empre.razao_social}</span>
            <span role="button" tabIndex={0} data-testid="eql-refresh-button" title="Atualizar lista de EQL"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); refreshEqls(); }}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); refreshEqls(); } }}
              className="flex items-center gap-1 text-xs text-indigo-400 hover:text-indigo-300 rounded-md px-2 py-1 hover:bg-zinc-800 transition-colors duration-200">
              {refreshing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              EQL
            </span>
            <ChevronDown className="chev h-4 w-4 text-zinc-500 transition-transform duration-200" />
          </CollapsibleTrigger>
          <CollapsibleContent className="px-5 pb-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 pt-1 border-t border-zinc-800/60" data-testid="empreendimento-data">
              <div className="pt-3">
                <p className="text-[11px] text-zinc-500 mb-1">Ativo</p>
                <span className="flex items-center gap-1.5" data-testid="empreendimento-ativo">
                  <span className={`h-2.5 w-2.5 rounded-full ${empre.ativo ? "bg-emerald-400" : "bg-rose-500"}`} />
                  <span className={`text-sm ${empre.ativo ? "text-emerald-400" : "text-rose-400"}`}>{empre.ativo ? "Sim" : "Não"}</span>
                </span>
              </div>
              <div className="pt-3">
                <p className="text-[11px] text-zinc-500 mb-1">CNPJ</p>
                <p className="text-sm text-zinc-200" data-testid="empreendimento-cnpj">{empre.cnpj || "—"}</p>
              </div>
              <div className="pt-3">
                <p className="text-[11px] text-zinc-500 mb-1">Apelido</p>
                <p className="text-sm text-zinc-200" data-testid="empreendimento-apelido">{empre.apelido || "—"}</p>
              </div>
              <div className="pt-3">
                <p className="text-[11px] text-zinc-500 mb-1">Cidade</p>
                <p className="text-sm text-zinc-200" data-testid="empreendimento-cidade">{empre.cidade || "—"}</p>
              </div>
              <div className="pt-3">
                <p className="text-[11px] text-zinc-500 mb-1">Origem</p>
                <p className="text-sm text-zinc-200" data-testid="empreendimento-origem">{empre.codigo_origem ?? "—"}</p>
              </div>
            </div>
            {eqlStatus && (
              <p className="text-[11px] text-zinc-500 mt-3" data-testid="eql-sync-status">
                {eqlStatus.count} EQL(s) sincronizada(s){eqlStatus.synced_at ? ` em ${fmtDate(eqlStatus.synced_at)}` : ""}{eqlStatus.syncing ? " — atualizando em segundo plano…" : ""}
              </p>
            )}
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
}
